import { useEffect, useMemo } from "react";
import { getQueryKey } from "@trpc/react-query";
import { useQuery } from "@tanstack/react-query";
import type { z } from "zod";
import { RouterOutputs, trpc } from "@utils/trpc";
import { NoAuthError } from "@utils/NoAuthError";
import { useData, useDataAuth } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { isAccessNetworkError } from "./accessCache";
import { accessTicketSchema, accessTicketsListSchema } from "@libs/wire";
import { accessesListAllQuery } from "@hooks/trpc/useAccesses";

type ManagedAccess = RouterOutputs["user"]["managedArtists"]["getAccessById"];
type ManagedAccesses =
  RouterOutputs["user"]["managedArtists"]["listCreatedAccesses"];

/**
 * Bidirectional drift checks pinning the wire schemas to the router
 * output. See `useLibrary/queryFunctions.ts` for the full rationale.
 */
type _ManagedAccessWireSchemaSubset =
  z.infer<typeof accessTicketSchema> extends ManagedAccess ? true : never;
type _ManagedAccessWireSchemaSuperset =
  ManagedAccess extends z.infer<typeof accessTicketSchema> ? true : never;
const _managedAccessWireSchemaDriftCheck: [
  _ManagedAccessWireSchemaSubset,
  _ManagedAccessWireSchemaSuperset,
] = [true, true];
void _managedAccessWireSchemaDriftCheck;

type _ManagedAccessesWireSchemaSubset =
  z.infer<typeof accessTicketsListSchema> extends ManagedAccesses
    ? true
    : never;
type _ManagedAccessesWireSchemaSuperset =
  ManagedAccesses extends z.infer<typeof accessTicketsListSchema>
    ? true
    : never;
const _managedAccessesWireSchemaDriftCheck: [
  _ManagedAccessesWireSchemaSubset,
  _ManagedAccessesWireSchemaSuperset,
] = [true, true];
void _managedAccessesWireSchemaDriftCheck;

export const getAccessByIdQueryKey = (accessId: string) =>
  getQueryKey(trpc.user.managedArtists.getAccessById, accessId, "query");

export const listCreatedAccessesQueryKey = () =>
  getQueryKey(trpc.user.managedArtists.listCreatedAccesses, undefined, "query");

/**
 * Adds an access to the cache of accesses list — the optimistic write-
 * through pattern (Phase 2.2). Both the in-memory react-query cache and the
 * SQLite durable cache get the new access shape, so a kill-and-relaunch
 * doesn't revert the post-update state.
 *
 * (The pre-Phase-2.7 implementation of this function recursed into itself
 * — a shipping infinite-loop that was effectively dead because no caller
 * exercised the path. Cleaned up here.)
 */
const updateAccessesCache = (
  data: DataSession,
  managedAccess: ManagedAccess,
) => {
  const { queryClient } = data;
  const qKeyCreated = listCreatedAccessesQueryKey();

  const createdData = queryClient.getQueryData<ManagedAccesses>(qKeyCreated);
  if (createdData) {
    data.cache.setQueryDataDurable<ManagedAccesses>(
      queryClient,
      qKeyCreated,
      (current) =>
        current?.map((x) =>
          x.id === managedAccess.id ? { ...managedAccess } : x,
        ),
    );
  }
};

/**
 * Updates the cache of of a single access — write-through to durable.
 */
const updateAccessCache = (
  data: DataSession,
  accessId: string,
  managedAccess: ManagedAccess,
) => {
  const { queryClient } = data;
  data.cache.setQueryDataDurable<ManagedAccess>(
    queryClient,
    getAccessByIdQueryKey(accessId),
    () => managedAccess,
  );
};

const managedAccessByIdQuery = (data: DataSession, accessId: string) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isAccessNetworkError,
    queryKey: getAccessByIdQueryKey(accessId),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("managedAccessByIdQuery queryFn");
      }
      return data.api.user.managedArtists.getAccessById.query(accessId);
    },
    schema:
      accessTicketSchema as unknown as import("zod").z.ZodType<ManagedAccess>,
  });

const listCreatedAccessesQuery = (data: DataSession) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isAccessNetworkError,
    queryKey: listCreatedAccessesQueryKey(),
    queryFn: () => {
      if (!data.auth.currentUser()) {
        throw new NoAuthError("listCreatedAccessesQuery queryFn");
      }
      return data.api.user.managedArtists.listCreatedAccesses.query();
    },
    schema:
      accessTicketsListSchema as unknown as import("zod").z.ZodType<ManagedAccesses>,
  });

/**
 * Get a single access
 */
const useManagedAccess = (
  accessId: string | undefined,
  refetchInterval?: number,
) => {
  const data = useData();
  const enabled = !!useDataAuth().firebaseUser && !!accessId;
  const query = useQuery({
    ...managedAccessByIdQuery(data, accessId || ""),
    enabled,
    refetchInterval: refetchInterval || false,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (query.data) {
      updateAccessesCache(data, query.data);
    }
  }, [data, query.data]);

  return query;
};

/**
 * Get a list of accesses - all, joined, created
 */
const useManagedAccesses = (type: "all" | "joined" | "created") => {
  const data = useData();
  const enabled = !!useDataAuth().firebaseUser;

  // Note: These are public accesses
  const allQuery = useQuery({
    ...accessesListAllQuery(data),
    enabled: type === "all" && enabled,
    refetchOnMount: "always",
  });

  // Note: These are managed accesses
  const createdQuery = useQuery({
    ...listCreatedAccessesQuery(data),
    enabled: type === "created" && enabled,
    refetchOnMount: "always",
  });

  const query = useMemo(() => {
    if (type === "all") {
      return allQuery;
    } else if (type === "created") {
      return createdQuery;
    } else {
      throw new Error(`Invalid managedAccesses type: ${type}`);
    }
  }, [allQuery, createdQuery, type]);

  useEffect(() => {
    if (query.data) {
      query.data.forEach((x) => {
        updateAccessCache(data, x.id, x);
      });
    }
  }, [data, query.data]);

  return query;
};

export { useManagedAccess, useManagedAccesses };
